import { computed, watchEffect } from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { useImage } from '@/composables/useImage'
import { IbexaImageVariations } from '@/enums/IbexaImageVariations'
import { useContentApi } from '@/composables/useCmsContentApi'
import { createQueryKey } from '@/utils/utils'
import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
import type { SubjectResource } from '@/types/Subject'
import type { TaskFrontpage } from '@/types/Task'

// Second argument can be deleted after VGS-473
export const useTaskFrontpage = (contentId: number, isFallbackTextImage = false) => {
  const { getCurrentElement, getCurrentElementByLocationId, getResourceGuideChild } = useContentApi()
  const { setImageOptions, src, altText, caption, copyright } = useImage()

  const taskFrontpageQuery = useQuery<TaskFrontpage>({
    queryKey: createQueryKey('getCurrentElement', contentId),
    queryFn: () => getCurrentElement(contentId),
    enabled: computed(() => !!contentId),
    staleTime: Infinity,
  })

  const parentElementQuery = useQuery<SubjectResource>({
    queryKey: ['getCurrentElementByLocationId', taskFrontpageQuery.data],
    queryFn: () => getCurrentElementByLocationId<SubjectResource>(taskFrontpageQuery.data.value?.parentLocationId as string),
    enabled: computed(() => !!taskFrontpageQuery.isSuccess.value),
    staleTime: Infinity,
  })

  const resourceGuideQuery = useQuery<AnchorNavigationPage | null>({
    queryKey: ['getResourceGuideChild', parentElementQuery.data],
    queryFn: () => getResourceGuideChild(parentElementQuery.data.value?.contentId as number),
    enabled: computed(() => !!parentElementQuery.isSuccess.value && !isFallbackTextImage),
    staleTime: Infinity,
  })

  const taskFrontpage = computed(() => taskFrontpageQuery.data?.value)
  const parentElement = computed(() => parentElementQuery.data?.value)
  const resourceGuide = computed(() => resourceGuideQuery.data?.value)

  watchEffect(() => setImageOptions({
    imageId: taskFrontpage.value?.image?.destinationContentId ?? '',
    svgId: taskFrontpage.value?.svg?.destinationContentId ?? '',
    imageVariation: IbexaImageVariations.FullWidth,
  }))

  return {
    taskFrontpage,
    parentElement,
    resourceGuide,
    src,
    altText,
    caption,
    copyright,
  }
}